import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BehaviorSubject } from 'rxjs';

import { I18nService } from './i18n.service';

// Authentication for the customer portal.
//
// Firebase Auth is used purely as an identity provider here — the portal never
// reads Firestore directly. Whether this account may see anything at all, and
// which customers, is decided server-side from the `portalUsers/{uid}` doc on
// every request (services/portalService.js). So a successful Firebase sign-in
// is NOT yet a successful portal sign-in: /portal/me has to accept it too,
// which is why signIn() finishes by loading the session and signs the user
// straight back out if the API rejects them.
@Injectable({ providedIn: 'root' })
export class PortalAuthService {

  // The /portal/me payload: user, customers, groups. Null when signed out.
  session$ = new BehaviorSubject<any>(null);

  constructor(private afAuth: AngularFireAuth, private router: Router, private i18n: I18nService) { }

  get session(): any { return this.session$.value; }

  async getToken(): Promise<string | null> {
    const user = await this.afAuth.currentUser;
    if (!user) { return null; }
    return user.getIdToken();
  }

  async signIn(email: string, password: string): Promise<void> {
    try {
      await this.afAuth.signInWithEmailAndPassword(email.trim(), password);
    } catch (err: any) {
      throw new Error(this.describeAuthError(err));
    }
  }

  async sendPasswordReset(email: string): Promise<void> {
    try {
      await this.afAuth.sendPasswordResetEmail(email.trim());
    } catch (err: any) {
      // Deliberately vague on user-not-found: the reset form must not be usable
      // to discover which email addresses have portal access.
      if (err && err.code === 'auth/user-not-found') { return; }
      throw new Error(this.describeAuthError(err));
    }
  }

  setSession(session: any) { this.session$.next(session); }

  async signOut(redirect = true) {
    this.session$.next(null);
    await this.afAuth.signOut();
    if (redirect) { this.router.navigate(['/login']); }
  }

  private describeAuthError(err: any): string {
    switch (err && err.code) {
      case 'auth/invalid-email': return this.i18n.t('login.errorInvalidEmail');
      case 'auth/user-disabled': return this.i18n.t('login.errorAccountDisabled');
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential': return this.i18n.t('login.errorWrongCredentials');
      case 'auth/too-many-requests': return this.i18n.t('login.errorTooManyAttempts');
      default: return this.i18n.t('login.errorGeneric');
    }
  }
}
